import { noop } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { slices } from '~/redux/slices';
import { Uuid } from '~/types/Uuid';

import { useGetLibraryModelVersion } from '../../library/hooks';

const ADDITIONAL_INSTRUCTIONS_CHARACTER_LIMIT = 300;

interface Props {
  libraryModelId: Uuid;
  libraryModelVersion: string; // e.g. "1.0.0"
}

export const LibraryModelInstructions = ({
  libraryModelId,
  libraryModelVersion,
}: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { model, isLoading } = useGetLibraryModelVersion({
    libraryModelId,
    libraryModelVersion,
  });

  const instructions = useSelector(slices.xRay.selectors.selectInstructions);

  if (!model) return null;

  return (
    <ContentContainer loadingContent={{ isLoading }}>
      <>
        <FormField<string, false>
          readOnly
          input={TextArea}
          label={intl.formatMessage({
            id: 'XRayConfiguration.libraryModelDescriptionSummary.label',
            defaultMessage: 'Library Model Instructions',
            description:
              'Label for the read-only library model description summary field',
          })}
          name="library-model-description-summary"
          value={model.description}
          onChange={noop}
        />
        <FormField<string, false>
          optional
          description={intl.formatMessage({
            id: 'XRayConfiguration.libraryModelInstructions.description',
            defaultMessage: 'Provide additional guidance to the Library Model.',
            description:
              'Description for the field to add additional library model instructions',
          })}
          footer={intl.formatMessage(
            {
              id: 'XRayConfiguration.libraryModelInstructions.limitText',
              defaultMessage: '{length}/{limit} characters',
              description:
                'Character limit text for the additional instructions field. Example: length = 125, limit = 500',
            },
            {
              length: instructions?.length ?? 0,
              limit: ADDITIONAL_INSTRUCTIONS_CHARACTER_LIMIT,
            },
          )}
          input={TextArea}
          inputProps={{ maxLength: ADDITIONAL_INSTRUCTIONS_CHARACTER_LIMIT }}
          label={intl.formatMessage({
            id: 'XRayConfiguration.libraryModelInstructions.label',
            defaultMessage: 'Add Instructions',
            description:
              'Label for the field to add additional library model instructions',
          })}
          name="library-model-additional-instructions"
          value={instructions}
          onChange={(updatedInstructions) =>
            dispatch(slices.xRay.actions.setInstructions(updatedInstructions))
          }
        />
      </>
    </ContentContainer>
  );
};
